"use client";

import { useEffect, useState } from "react";
import { getForumUser, ForumSessionUser } from "./PublicAuth";
import { JoinModal, authedFetch, participationHint } from "./PublicEngage";

// Report a thread or a reply. Same reasons and endpoint as the CRM tab; the
// InsuraCentral team reviews the queue.
export const REPORT_REASONS: Array<[string, string]> = [
  ["spam", "Spam or repetitive content"],
  ["harassment", "Harassment or disrespect"],
  ["pii", "Client PII / compliance risk"],
  ["misinformation", "Misleading product or carrier claims"],
  ["self_promotion", "Excessive self-promotion / recruiting"],
  ["other", "Something else"],
];

export function PublicReport({ targetType, targetId }: { targetType: "post" | "comment"; targetId: string }) {
  const [user, setUser] = useState<ForumSessionUser | null>(null);
  const [open, setOpen] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [reason, setReason] = useState("spam");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<"reported" | "already" | null>(null);
  useEffect(() => setUser(getForumUser()), []);

  const start = () => {
    if (!user) {
      setShowJoin(true);
      return;
    }
    setOpen((o) => !o);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await authedFetch("/reports", {
        method: "POST",
        body: JSON.stringify({ targetType, targetId, reason, details: details.trim() || undefined }),
      });
      setDone(res?.alreadyReported ? "already" : "reported");
      setOpen(false);
    } catch (e: any) {
      setError(participationHint(e) || e.message || "Couldn't send the report.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <span className="fp-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">
        {done === "already" ? "Already reported" : "Reported · thanks"}
      </span>
    );
  }

  return (
    <>
      {showJoin && <JoinModal onClose={() => setShowJoin(false)} />}
      <button
        type="button"
        onClick={start}
        aria-expanded={open}
        className="fp-inline text-[12px] font-medium text-slate-500 underline-offset-2 hover:text-red-700 hover:underline"
      >
        Report
      </button>
      {open && (
        <div className="mt-2 w-full max-w-md rounded-md border border-slate-200 bg-white p-3 text-left shadow-sm" role="dialog" aria-label={`Report this ${targetType === "post" ? "thread" : "reply"}`}>
          <p className="text-[13px] font-semibold text-slate-900">Report this {targetType === "post" ? "thread" : "reply"}</p>
          <p className="mt-0.5 text-[12px] leading-4 text-slate-500">Reviewed by the InsuraCentral team.</p>
          <div className="mt-2.5 space-y-1.5">
            {REPORT_REASONS.map(([value, label]) => (
              <label key={value} className="flex cursor-pointer items-center gap-2 text-[13px] text-slate-700">
                <input type="radio" name={`report-${targetId}`} value={value} checked={reason === value} onChange={() => setReason(value)} className="h-3.5 w-3.5 accent-blue-600" />
                {label}
              </label>
            ))}
          </div>
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value.slice(0, 500))}
            rows={2}
            placeholder="Anything the reviewer should know (optional)"
            className="mt-2.5 w-full resize-y rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] leading-5 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          />
          {error && <p className="mt-1.5 text-[12px] text-red-700">{error}</p>}
          <div className="mt-2.5 flex items-center gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="inline-flex h-8 items-center rounded-md bg-red-600 px-3 text-[12px] font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-60"
            >
              {busy ? "Sending…" : "Send report"}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="fp-inline text-[12px] font-medium text-slate-500 hover:text-slate-900">
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
